import React, { useContext, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import I18n, { translate } from '@utils/i18n';
import { Button, Text } from '@ui';

import StatusChangeContext from './StatusChangeContext';

// Shared namespace, not features.proposals.index: this modal is also used by the
// proposal detail screen via ProposalStatusAction.
const namespace = 'features.proposals.status_modal';

// IP-15353 / Aha INS-I-108: change a proposal's status without walking the whole
// proposal workflow. Reached from the row's Actions menu on the listing.
//
// Statuses are not labels. Three of them run side effects through Proposal's
// callbacks, and one of those leaves the building:
//
//   accepted      -> PROPOSAL_ACCEPTED webhooks to external endpoints, and linked
//                    deficiencies become "Quote Accepted"
//   declined      -> linked deficiencies become "Quote Denied"
//   out_for_quote -> linked deficiencies become "Out for Quote"
//
// These already fire from the edit screen, so this adds no behaviour — but it makes
// them reachable in two clicks, so the modal says what will happen before you save.
// Draft, Viewed and any status the tenant added carry no side effects and get no
// warning.
export const STATUSES_WITH_SIDE_EFFECTS = ['accepted', 'declined', 'out_for_quote'];

function StatusModal({ dataItem, onClose }) {
  const { statuses, onChangeStatus } = useContext(StatusChangeContext);

  const current = useMemo(
    () => statuses.find((option) => option.value === dataItem.statusId) || null,
    [statuses, dataItem.statusId]
  );

  const [selected, setSelected] = useState(current);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const unchanged = !selected || selected.value === dataItem.statusId;
  // Only warn about a change actually being made. Without the `unchanged` guard the
  // modal opens already warning whenever the proposal's *current* status happens to
  // be one of these — a consequence notice for a change you have not made, next to
  // a disabled Save.
  const warning = selected && !unchanged && STATUSES_WITH_SIDE_EFFECTS.includes(selected.nameAbbr);

  // A request is already on its way to the server, so dismissing now would leave the
  // user looking at a row that is about to change with no idea it is coming. The X and
  // Cancel are inert until it resolves, and the failure path re-enables them.
  const dismiss = () => {
    if (saving) return;
    onClose();
  };

  const save = async () => {
    setSaving(true);
    setError(null);

    try {
      await onChangeStatus(dataItem.id, selected.value);
      onClose();
    } catch {
      // Kept open with the message rather than closed silently: the row still shows
      // the old status, so closing would look like the change had worked.
      setError(translate('error', { namespace }));
      setSaving(false);
    }
  };

  return (
    // width={500} matches the other single-field dialogs (RecordPaymentModal). Left to
    // size itself the dialog collapses to ~370px, which crowds the longer status names.
    <Dialog width={500} title={translate('title', { namespace })} className="qmb-dialog" onClose={dismiss}>
      {/* One section, not two: the proposal number and the field it applies to are a
          single block, and modal__section's 3.2rem padding between them reads as two
          unrelated groups. The gap between them is set here instead. */}
      <div className="modal__section" style={{ display: 'flex', flexDirection: 'column', gap: '1.6rem' }}>
        {/* Text rather than a bare <p>: a raw paragraph inherits the legacy page font
            instead of the design system's, which shows next to the label below it. */}
        <Text variant="body" as="p" style={{ margin: 0 }}>
          {translate('description', { namespace, number: dataItem.number })}
        </Text>

        <div>
          <Text
            variant="label"
            as="label"
            htmlFor="proposal-status"
            style={{ display: 'block', marginBottom: '0.4rem' }}>
            {translate('label', { namespace })}
          </Text>
          {/* qmb-select--blank is what gives the Kendo picker width:100% and the right
              padding. Without the wrapper it sizes itself to its content and pushes past
              the dialog's padding once a long status is selected. --blank because the
              label sits above rather than floating inside. */}
          <div className="qmb-select--blank">
            <DropDownList
              id="proposal-status"
              data={statuses}
              textField="title"
              dataItemKey="value"
              value={selected}
              disabled={saving}
              onChange={(event) => setSelected(event.target.value)}
              itemRender={(li, itemProps) =>
                React.cloneElement(
                  li,
                  li.props,
                  <>
                    <span className="qmb-avatar--24--status-icon">
                      <i className={`fa-lg ${itemProps.dataItem.icon}`} style={{ color: itemProps.dataItem.color }} />
                    </span>
                    <span style={{ marginLeft: '0.5rem' }}>{itemProps.dataItem.title}</span>
                  </>
                )
              }
            />
          </div>
        </div>
      </div>

      {warning && (
        <div className="modal__section">
          <ul className="cutout__banner">
            <li className="banner__item--warn">
              {translate(`warning.${selected.nameAbbr}`, { namespace, status: selected.title })}
            </li>
          </ul>
        </div>
      )}

      {error && (
        <div className="modal__section">
          <ul className="cutout__banner">
            <li className="banner__item--error">{error}</li>
          </ul>
        </div>
      )}

      <div className="modal__footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Button onClick={dismiss} disabled={saving}>
          {I18n.t('generic.cancel')}
        </Button>
        <Button
          variant="primary"
          disabled={saving || unchanged}
          onClick={() => {
            // eslint-disable-next-line no-void
            void save();
          }}>
          {translate('save', { namespace })}
        </Button>
      </div>
    </Dialog>
  );
}

StatusModal.propTypes = {
  dataItem: PropTypes.shape({
    id: PropTypes.number.isRequired,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    statusId: PropTypes.number
  }).isRequired,
  onClose: PropTypes.func.isRequired
};

export default StatusModal;
